import Axios from "helpers/Interceptor"
import * as Sentry from "@sentry/browser"
import {
    CurrentBrand,
    YouTubeChannel,
    SpotifyArtistSearchResponse,
    TikTokProfileForOnboarding,
    InstagramProfileForOnboarding,
    BrandCurrency,
    CampaignDetails as CampaignDetailsType,
    CampaignMetadata,
    TikTokAdAccount,
    TikTokSparkAd,
    EmailCampaignData,
    Fan,
    MappedColumns,
    FetchFanbaseParams,
    BrandContent
} from 'modules/Types'
import { FanbaseMetricsType } from "pages/DashboardPage/DashboardPage"
import { PlanType } from "modules/Const"

export type CreateBrandPayload = {
    name: string | null;
    tier?: PlanType;
    customization: {
        youtube_channel?: YouTubeChannel;
        spotify_artist?: SpotifyArtistSearchResponse;
        tiktok?: TikTokProfileForOnboarding;

        // we don't validate the instagram profile for onboarding to check if its a
        // business or creator account, or personal account. to pull data,
        // it needs to be a business account.
        instagram_profile_for_onboarding?: InstagramProfileForOnboarding;
        business_discovery_instagram?: {
            url: string;
            username: string;
            picture?: string;
        };
        chartmetric?: {
            id: string;
        };
        onboarding_answers?: Record<string, any>;
    }
}

/**
 * Retrieves all brands associated with the current user
 */
export const getBrands_api = async (): Promise<CurrentBrand[]> => {
    try {
        Sentry.setTag("flow", "getUserBrands")

        const response = await Axios.get('/brand')

        const brandsArray = response.data
        return brandsArray
    } catch (error) {
        Sentry.captureException(error)
        return []
    }
}

/** Interface for connectToBrand function parameters */
interface ConnectToBrandParams {
    /** ID of the brand to update */
    brandId: CurrentBrand['id'];
    /** URL-friendly slug name of the brand */
    brandSlug: CurrentBrand['slug'];
    /** Value/data to update for the service */
    value: any;
    /** Type of service being connected (e.g. 'facebook', 'instagram', etc) */
    service: string;
}

/**
 * Updates a specific service connection for a brand
 * @param params - Parameters for connecting service to brand
 * @returns Promise resolving to the updated brand data or null if error occurs
 */
export const connectToBrand_api = async ({
    brandId,
    brandSlug,
    value,
    service,
}: ConnectToBrandParams): Promise<CurrentBrand> => {
    try {
        Sentry.setTag('flow', 'updateInBrand')
        Sentry.setContext('input', {
            brandId,
            brandSlug,
            value,
            service
        })

        const response = await Axios.put<CurrentBrand>(`/brand/${brandSlug}/connect`, {
            id: brandId,
            name: brandSlug,
            service,
            value,
        })
        return response.data
    } catch (error) {
        Sentry.captureException(error)
        throw error
    }
}


/**
 * Disconnects Facebook accounts from a brand
 * @async
 * @param {string} brandSlug - The slug/URL-friendly name of the brand
 * @param {number} brandId - The ID of the brand
 * @param {string} brandName - The name of the brand
 * @returns {Promise<void>}
 */
export const disconnectAccounts_api = async (brandSlug: CurrentBrand['slug'], brandId: CurrentBrand['id'], brandName: CurrentBrand['name']) => {
    await Axios.put(`/brand/${brandSlug}/disconnect`, {
        id: brandId,
        name: brandName,
        service: 'facebook'
    })
}

/**
 * Checks if a Spotify artist already exists for the current user in the system
 * @param spotifyArtistId - The Spotify artist ID to check. Can be null.
 * @returns Promise containing either the found brand data or error details
 * @throws Will capture exception in Sentry if API call fails
 */
export const checkIfSpotifyArtistExistsForUser_api = async (
    spotifyArtistId: string | null
): Promise<{
    // Response data containing brand info if found
    data: CurrentBrand | null;
    // Error details if request fails
    error: unknown | null;
}> => {
    // Return early if no Spotify ID provided
    if (!spotifyArtistId) {
        return { data: null, error: null }
    }

    try {
        // Make API call to check if artist exists
        const response = await Axios.get<CurrentBrand>(`/brand/spotify/${spotifyArtistId}`)
        return { data: response.data, error: null }
    } catch (error) {
        // Log error and return null data with error
        console.error("Error checking if Spotify artist exists:", error)
        Sentry.captureException(error)
        return { data: null, error }
    }
}

/**
 * Interface representing user data structure
 * @interface BrandUser
 * @property {string} email - User's email address
 * @property {string} [name] - User's name (optional)
 * @property {string} [role] - User's role in the system
 */
export interface BrandUser {
    id: string
    name?: string
    email?: string
    isOwner?: boolean
    permissionLevel?: string
}

/**
 * Interface for user data response
 * @interface BrandUsersApiResponse
 * @property {BrandUser[]} users - Array of user data
 * @property {number} availableSeats - Number of available seats in the organization
 */
export interface BrandUsersApiResponse {
    users: BrandUser[];
    availableSeats: number;
}

/**
 * Fetches users associated with a specific brand
 * @async
 * @param {Object} params - Parameters for fetching users
 * @param {number} [params.brandId] - ID of the brand to fetch users for
 * @returns {Promise<BrandUsersApiResponse>}
 */
export const getBrandUsers_api = async ({
    brandId,
}: {
    brandId?: number | null;
}): Promise<BrandUsersApiResponse> => {
    try {
        const response = await Axios.get<BrandUsersApiResponse>(`/brand/${brandId}/users`);
        if (!response?.data) {
            throw new Error('Error fetching brand users');
        }
        const { users, availableSeats } = response.data;
        return { users, availableSeats };
    } catch (error) {
        console.error('Error fetching brand users:', error);
        // TODO: Implement proper error handling
        throw error;
    }
};

/**
 * Adds a new user to a brand
 * @async
 * @param {Object} params - Parameters for adding a user to a brand
 * @param {number} params.brandId - ID of the brand to add user to
 * @param {BrandUser[]} params.users - Array of users to add to the brand
 * @returns {Promise<BrandUsersApiResponse>} - Response containing updated users list and available seats
 */
export const createUserBrand_api = async ({
    brandId,
    email,
}: {
    brandId: number;
    email: string;
}): Promise<BrandUser> => {
    try {
        const response = await Axios.post(`/brand/${brandId}/users`, { email });
        if (!response?.data) {
            throw new Error('Error adding brand users');
        }
        const { data: addedUser } = response.data;
        return addedUser;
    } catch (error) {
        console.error('Error adding brand users:', error);
        throw error;
    }
};

/**
 * Removes a user from a brand
 * @async
 * @param {Object} params - Parameters for deleting a user from a brand
 * @param {number} params.brandId - ID of the brand to remove user from
 * @param {BrandUser} params.potentiallyDeletedUser - User to be removed from the brand
 * @returns {Promise<BrandUsersApiResponse>} - Response containing updated users list and available seats
 */
export const deleteUserFromBrand_api = async ({
    brandId,
    userId,
}: {
    brandId: number;
    userId: string;
}): Promise<BrandUsersApiResponse> => {
    try {
        const res = await Axios.delete(`/brand/${brandId}/users/${userId}`);

        if (!res?.data) {
            throw new Error('Error deleting brand user');
        }

        return res.data
    } catch (error) {
        console.error('Error deleting brand user:', error);
        throw error;
    }
};

/**
 * Creates a new brand with the provided payload
 * @async
 * @param {CreateBrandPayload} payload - The brand creation payload containing name and customization options
 * @param {string | null} payload.name - Name of the brand
 * @param {Object} payload.customization - Customization options for the brand
 * @param {YouTubeChannel} [payload.customization.youtube_channel] - YouTube channel details
 * @param {SpotifyArtistSearchResponse} [payload.customization.spotify_artist] - Spotify artist details
 * @param {TikTokProfileForOnboarding} [payload.customization.tiktok] - TikTok account details
 * @param {Object} [payload.customization.business_discovery_instagram] - Instagram business account details
 * @param {Object} [payload.customization.chartmetric] - Chartmetric details
 * @param {Record<string, any>} [payload.customization.onboarding_answers] - Onboarding survey answers
 * @returns {Promise<BrandCreationResponse>} The created brand data
 */
export const createBrand_api = async (payload: CreateBrandPayload): Promise<CurrentBrand> => {
    try {
        const response = await Axios.post<CurrentBrand>('/brand', payload, {
            timeout: 60 * 1000
        });

        console.log("response", response.data)
        return response.data;
    } catch (error) {
        console.error('Error creating brand:', error);
        throw error;
    }
};

export const updateBrand_api = async (brandId: CurrentBrand['id'], payload: any) => {
    try {
        const response = await Axios.put<CurrentBrand>(`/brand/${brandId}`, payload);
        return response.data;
    } catch (error) {
        throw error;
    }
}
export const saveCurrency_api = async (currency: BrandCurrency) => {
    try {
        const response = await Axios.put('/brand/connect/currency', {
            currencyCode: currency.code,
            currencySymbol: currency.symbol
        });

        return response.data;
    } catch (error) {
        throw error;
    }
}

export const searchBrands_new_api = async ({ name, slug, brandId }: { name?: string, slug?: string, brandId?: number }) => {
    try {
        const response = await Axios.post(`/brand/searchbrands`, {
            name,
            slug,
            brandId,
        });
        return response.data;
    } catch (error) {
        throw error;
    }
}
/**
 * Retrieves a single brand by its ID
 * @async
 * @param {number} brandId - The ID of the brand to retrieve
 * @returns {Promise<CurrentBrand | null>} The brand data or null if not found/error occurs
 */
export const getBrand_api = async (brandId: CurrentBrand['id']): Promise<CurrentBrand | null> => {
    try {
        Sentry.setTag('flow', 'getBrand')

        const response = await Axios.get<CurrentBrand>(`/brand/${brandId}`)
        return response.data
    } catch (error) {
        console.error('Error fetching brand:', error)
        Sentry.captureException(error)
        return null
    }
}

/** Interface for mapping CSV column headers to Fan data fields */
interface MappedHeaders {
    CellPhone?: string;
    City?: string;
    Country?: string;
    Email?: string;
    LastName?: string;
    Name?: string;
    Source?: string;
    State?: string;
}

/** Interface for fan preview parameters */
interface GetFansPreviewParams {
    /** Page number for pagination */
    page?: number;
    /** Mapped column headers */
    mappedHeaders?: MappedHeaders;
    /** Number of records to return */
    first?: number;
    /** Source of the fans data */
    source: string;
    /** Email list identifier */
    email_list?: string;
}

/** Interface for importing fans payload */
interface ImportFansPayload {
    /** Email list identifier */
    email_list?: string;
    /** Mapped column headers */
    mappedHeaders?: MappedColumns;
    /** Source of the fans data */
    source: string;
}

/** Interface for campaign details */
interface EmailCampaignDetails {
    id: number;
    subject_line: string;
    html_content: string;
    sent_at: string;
    status: string;
    metrics: {
        opened: number;
        clicked: number;
        delivered: number;
        total: number;
    };
}

/**
 * Retrieves CSV headers for a brand
 * @param brandId - ID of the brand
 * @returns Promise containing the CSV headers or error
 */
export const getCsvHeaders_api = async (brandId: CurrentBrand['id']): Promise<{ data: string[] | null; error: unknown | null }> => {
    try {
        const response = await Axios.get<string[]>(`/brand/${brandId}/csv-headers`)
        return { data: response.data, error: null }
    } catch (error) {
        Sentry.captureException(error)
        return { data: null, error }
    }
}

/** Interface for CSV upload response */
interface CsvUploadResponse {
    /** Number of records successfully processed */
    processedCount: number;
    /** Number of records that failed */
    failedCount: number;
    /** Array of failed records with reasons */
    failedRecords?: Array<{
        row: number;
        reason: string;
    }>;
    /** Status of the upload */
    status: 'completed' | 'processing' | 'failed';
}

/** Interface for fans metrics */
interface FansMetrics2 {
    /** Total number of fans */
    totalFans: number;
    /** Growth metrics */
    growth: {
        /** Growth in last 7 days */
        lastWeek: number;
        /** Growth in last 30 days */
        lastMonth: number;
        /** Growth percentage */
        percentage: number;
    };
    /** Fan engagement metrics */
    engagement: {
        /** Email open rate */
        emailOpenRate: number;
        /** Click through rate */
        clickRate: number;
    };
}

/** Interface for importing fans status */
interface ImportingFansStatus {
    /** Current status of the import */
    status: 'pending' | 'processing' | 'completed' | 'failed';
    /** Total records to process */
    totalRecords?: number;
    /** Number of records processed */
    processedRecords?: number;
    /** Error message if failed */
    error?: string;
}

/**
 * Uploads a CSV file containing fan data
 * @param brandId - ID of the brand
 * @param formData - FormData containing the CSV file
 * @returns Promise resolving to upload response or error
 */
export const uploadCsv_api = async (
    brandId: CurrentBrand['id'],
    formData: FormData
): Promise<any> => {
    try {
        const response = await Axios.post<any>(
            `/brand/${brandId}/fans-upload-csv`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        );
        return { data: response.data, error: null };
    } catch (error) {
        console.error('Error uploading CSV:', error);
        return { data: null, error };
    }
};

/**
 * Gets the current status of fan data import
 * @param brandId - ID of the brand
 * @returns Promise resolving to import status or error
 */
export const getImportingNewFansStatus_api = async (
    brandId: CurrentBrand['id']
): Promise<{ data: ImportingFansStatus | null; error: unknown | null }> => {
    try {
        const response = await Axios.get<ImportingFansStatus>(`/brand/${brandId}/fans-import-status`);
        return { data: response.data, error: null };
    } catch (error) {
        console.error('Error getting import status:', error);
        return { data: null, error };
    }
};

/**
 * Gets metrics about the brand's fans
 * @param brandId - ID of the brand
 * @returns Promise resolving to fans metrics or error
 */
export const getFansMetrics_api = async (
    brandId: CurrentBrand['id']
): Promise<{ data: FansMetrics2 | null; error: unknown | null }> => {
    try {
        const response = await Axios.get<FansMetrics2>(`/brand/${brandId}/metrics/fans`);
        return { data: response.data, error: null };
    } catch (error) {
        console.error('Error getting fans metrics:', error);
        return { data: null, error };
    }
};

/**
 * Gets a preview of fans data for a brand
 * @param brandId - ID of the brand
 * @param params - Parameters for the preview request
 * @returns Promise containing the fans preview data or error
 */
export const getFansPreview_api = async (
    brandId: CurrentBrand['id'],
    params: GetFansPreviewParams
): Promise<{ data: any | null; error: unknown | null }> => {
    try {
        const response = await Axios.get(`/brand/${brandId}/fans-preview`, { params })
        return { data: response.data, error: null }
    } catch (error) {
        Sentry.captureException(error)
        return { data: null, error }
    }
}

/**
 * Imports fans data for a brand
 * @param brandId - ID of the brand
 * @param payload - Data for importing fans
 * @returns Promise containing the import response or error
 */
export const importFans_api = async (
    brandId: CurrentBrand['id'],
    payload: ImportFansPayload
): Promise<{ data: any | null; error: unknown | null }> => {
    try {
        const response = await Axios.post(`/brand/${brandId}/import-fans-from-csv`, payload)
        return { data: response.data, error: null }
    } catch (error) {
        Sentry.captureException(error)
        return { data: null, error }
    }
}


/**
 * Gets a signed URL for downloading fans data
 * @param brandId - ID of the brand
 * @returns Promise containing the signed URL or error
 */
export const getDownloadSignedUrl_api = async (brandId: CurrentBrand['id']): Promise<{ data: string | null; error: unknown | null }> => {
    try {
        const response = await Axios.get<string>(`brand/${brandId}/csv/fans`)
        return { data: response.data, error: null }
    } catch (error) {
        Sentry.captureException(error)
        return { data: null, error }
    }
}

/**
 * Retrieves fan releases data for a brand within a date range
 * @param brandId - ID of the brand
 * @param startDate - Start date for the data range
 * @param endDate - End date for the data range
 * @returns Promise containing the fan releases data or error
 */
export const getFanReleases_api = async (
    brandId: CurrentBrand['id'],
    startDate?: string,
    endDate?: string
): Promise<{ data: any | null; error: unknown | null }> => {
    try {
        const response = await Axios.get(`/brand/${brandId}/metrics/fans/releases`, {
            params: { startDate, endDate }
        })
        return { data: response.data, error: null }
    } catch (error) {
        Sentry.captureException(error)
        return { data: null, error }
    }
}

/** Base interface for campaign draft data */
interface BaseCampaignDraft {
    /** ID of existing draft if updating */
    id?: number | null;
    /** Associated brand ID */
    brand_id: number;
    /** Complete campaign metadata */
    campaign_metadata: CampaignMetadata;
}

/** Response interface for save draft operation */
interface SaveDraftResponse {
    /** Campaign data if successful */
    data: CampaignDetailsType | null;
    /** Error information if failed */
    error: unknown | null;
}

/** Parameters for saving campaign draft */
interface SaveCampaignDraftParams {
    /** ID of the brand */
    brandId: CurrentBrand['id'];
    /** Existing draft data if updating */
    campaignDraftData?: {
        id?: number;
        campaign_metadata?: Record<string, unknown>;
    } | null;
    /** Campaign metadata to save */
    campaignMetadata: Partial<CampaignMetadata>;
    /** Current brand information */
    currentBrand: CurrentBrand;
}

/**
 * Saves or updates a campaign draft
 * @param params - Parameters for saving the draft
 * @returns Promise containing the saved campaign data or error
 */
export const saveCampaignDraft_api = async (
    params: SaveCampaignDraftParams
): Promise<SaveDraftResponse> => {
    const { brandId, campaignDraftData, campaignMetadata, currentBrand } = params;

    try {
        const payload: BaseCampaignDraft = {
            id: campaignDraftData?.id || null,
            brand_id: brandId,
            campaign_metadata: {
                ...(campaignDraftData?.campaign_metadata as Record<string, unknown>),
                ...campaignMetadata,
                access_token: currentBrand?.connections?.facebook_page?.access_token || null,
            } as CampaignMetadata
        };

        const response = await Axios.put<CampaignDetailsType>(
            `campaign/brand/${brandId}/save-draft`,
            payload
        );

        return { data: response.data, error: null };
    } catch (error) {
        console.error('Error saving campaign draft:', error);
        Sentry.captureException(error);
        return { data: null, error };
    }
};


/** Interface for brand search parameters */
interface BrandSearchParams {
    /** Search term to filter brands */
    searchTerm: string;
    /** Type of search to perform (brand, campaignId, email, stripe_id) */
    type: 'brand' | 'campaignId' | 'email' | 'stripe_id';
}



/**
 * Searches for brands based on provided criteria
 * @param params - Search parameters including term and type
 * @returns Promise containing matching brands or error
 */
export const searchBrands_api = async (
    params: BrandSearchParams
): Promise<CurrentBrand[]> => {
    try {
        const response = await Axios.get<CurrentBrand[]>(
            `/brand/search?searchTerm=${params.searchTerm}&type=${params.type}`
        );

        if (response.isError) {
            throw new Error(response.message || 'Error searching brands');
        }

        return response.data;
    } catch (error) {
        console.error('Error searching brands:', error);
        Sentry.captureException(error);
        return [];
    }
};



/** Type for the daily metrics in fan engagement stats */
interface DailyMetric {
    totalFans: number;
    foreverSaves: number;
}


/**
 * Fetches fan engagement statistics for a brand within a date range
 * @param brandId - The ID of the brand to fetch stats for
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 */
export const getSymphonyPlatformMetrics_api = async (
    brandId: CurrentBrand['id'],
    startDate: string,
    endDate: string
): Promise<FanbaseMetricsType> => {
    const { data } = await Axios.get<FanbaseMetricsType>(
        `/brand/${brandId}/metrics/fans`,
        {
            params: {
                startDate,
                endDate
            }
        }
    );
    return data;
};

/**
 * Retrieves email campaigns for a brand
 * @param brandId - ID of the brand to fetch campaigns for
 * @returns Promise containing email campaigns or error
 */
export const getEmailCampaigns_api = async (
    brandId: number | undefined
): Promise<EmailCampaignData[]> => {
    try {
        if (!brandId) {
            throw new Error('Brand ID is required');
        }

        const response = await Axios.get<EmailCampaignData[]>(`/brand/${brandId}/email-campaigns`);
        const data = response?.data;

        return data
    } catch (error) {
        Sentry.captureException(error);
        console.error('Error fetching email campaigns:', error);
        return []
    }
};

/**
 * Retrieves fanbase data for a brand with optional filtering parameters
 * @param brandId - ID of the brand
 * @param params - Optional parameters for filtering fanbase
 * @returns Promise containing fanbase data or error
 */
export const getFanbase_api = async (
    brandId: number,
    params?: FetchFanbaseParams
): Promise<{ fanbase: any[], count: number }> => {
    try {
        const response = await Axios.get<{ fanbase: Fan[], count: number }>(`brand/${brandId}/fanbase`, {
            params,
            timeout: 120000
        });

        const data = response?.data;
        return data
    } catch (error) {
        Sentry.captureException(error);
        console.error('Error fetching fanbase:', error);
        return { fanbase: [], count: 0 }
    }
};

export const getEmailUsage_api = async (brandId: number): Promise<{ usage: number }> => {
    const response = await Axios.get<{ usage: number }>(`brand/${brandId}/email-usage`);
    return response?.data;
};

/** Interface for fan data export response */
interface FanDataExportResponse {
    /** URL to download the exported data */
    downloadUrl: string;
    /** Expiry time of the download URL */
    expiresAt: string;
    /** Format of the export */
    format: 'csv' | 'xlsx';
}

/** Interface for fan data export options */
interface ExportFanDataOptions {
    /** Format to export in */
    format: 'csv' | 'xlsx';
    /** Optional filters to apply */
    filters?: {
        /** Date range for fan acquisition */
        dateRange?: {
            start: string;
            end: string;
        };
        /** Fan source/platform */
        source?: string[];
        /** Fan location */
        location?: string[];
    };
}

/**
 * Exports fan data in specified format
 * @param brandId - ID of the brand
 * @param options - Export options including format and filters
 * @returns Promise resolving to export response or error
 */
export const exportFanData_api = async (
    brandId: CurrentBrand['id'],
    options: ExportFanDataOptions
): Promise<{ data: FanDataExportResponse | null; error: unknown | null }> => {
    try {
        const response = await Axios.post<FanDataExportResponse>(
            `/brand/${brandId}/fans/export`,
            options
        );
        return { data: response.data, error: null };
    } catch (error) {
        console.error('Error exporting fan data:', error);
        return { data: null, error };
    }
};

export const getInstagramBusinessOrCreatorAccount_api = async (
    brandId: CurrentBrand['id'] | undefined,
    params: {
        username: string
    }
): Promise<any> => {
    if (!brandId) {
        throw new Error('Brand ID is required');
    }

    try {
        const response = await Axios.get<CurrentBrand>(`/brand/${brandId}/instagram-business-or-creator?username=${params.username}`);
        return response;
    } catch (error) {
        console.error('Error connecting Instagram business/creator account:', error);
        throw error;
    }
};

export const removeOrAddUserToBrand_api = async (brandId: CurrentBrand['id'], userId: string) => {
    try {
        const response = await Axios.post(`/brand/${brandId}/remove-or-add-user/${userId}`);
        return response.data;
    } catch (error) {
        console.error('Error removing or adding user to brand:', error);
        throw error;
    }
};



/**
 * Gets brand content by brand ID
 * @param brandId - ID of the brand
 * @param includePresaveReleases - Whether to include presave releases (which aren't released yet)
 * @returns Brand content data if successful, null otherwise
 */
export const getReleasesByBrandId_api = async ({
    brandId,
    includePresaveReleases = false,
}: {
    brandId: number;
    includePresaveReleases?: boolean;
}): Promise<BrandContent[] | null> => {
    try {
        const response = await Axios.get<BrandContent[]>(`/brand/${brandId}/releases`, {
            params: {
                includePresaveReleases
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error getting brand content by ID:', error);
        return null;
    }
}

/**
 * Gets brand content by brand ID
 * @param brandId - ID of the brand
 * @param includePresaveReleases - Whether to include presave releases (which aren't released yet)
 * @returns Brand content data if successful, null otherwise
 */
export const getWebsitesByBrandId_api = async ({
    brandId,
    includeLinkedCampaigns = false,
}: {
    brandId: number;
    includeLinkedCampaigns?: boolean;
}): Promise<BrandContent[] | null> => {
    try {
        const response = await Axios.get<BrandContent[]>(`/brand/${brandId}/websites`, {
            params: {
                includeLinkedCampaigns
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error getting brand content by ID:', error);
        return null;
    }
}


// Define the Invoice interface based on expected API response
export interface Invoice {
    id: string;
    amount_due: number;
    amount_paid: number;
    amount_remaining: number;
    currency: string;
    status: string; // e.g., 'paid', 'open', 'void'
    due_date: string | null; // Assuming due_date can be null
    created: number; // Assuming created is a timestamp
    // Add any other relevant fields from the API response
    invoice_pdf?: string; // URL for the invoice PDF, optional
    hosted_invoice_url?: string; // URL to Stripe hosted invoice page, optional
    customer_email?: string; // Optional customer email
    payment_description?: string; // Payment description from charge or payment_intent
    payment_name?: string; // Payment name from charge or payment_intent
}

/**
 * Fetches invoices for a specific brand.
 * @param brandId - The ID of the brand.
 * @returns A promise that resolves to an array of invoices.
 */
export const getBrandInvoices_api = async (brandId: string): Promise<Invoice[]> => {
    try {
        const response = await Axios.get(`/brand/${brandId}/invoices`);
        // Assuming the API returns data in the shape of { data: { invoices: Invoice[] } }
        // or directly { data: Invoice[] } or just Invoice[]
        // Adjust this based on the actual API response structure.
        // For this example, let's assume it's response.data.invoices
        // If the API returns invoices directly in response.data, use:
        // return response.data as Invoice[];
        if (response.data && Array.isArray(response.data.invoices)) {
            return response.data.invoices as Invoice[];
        } else if (Array.isArray(response.data)) {
            return response.data as Invoice[];
        } else {
            // Handle cases where the structure is not as expected
            console.error('Unexpected API response structure for invoices:', response.data);
            Sentry.captureMessage(`Unexpected API response structure for brand invoices: ${brandId}`);
            return []; // Return empty array or throw error
        }
    } catch (error) {
        console.error(`Error fetching invoices for brand ${brandId}:`, error);
        Sentry.captureException(error, {
            extra: { brandId, context: 'getBrandInvoices_api' },
        });
        // Depending on how you want to handle errors, you might re-throw,
        // return an empty array, or a specific error object.
        // For now, returning an empty array to allow the UI to handle 'no invoices'.
        return [];
    }
};
